import React, { type SyntheticEvent, useMemo } from "react";
import cn from "classnames";

import CardThumbnail from "../../../../components/cards/cardThumbnail/CardThumbnail";
import CardContent from "../../../../components/cards/cardContent/CardContent";
import { PublishStatus } from "components/common/PublishStatus";
import EllipsisPopupButton from "../../../../components/buttons/ellipsisPopupButton/EllipsisPopupButton";
import type Icon from "../../../../interfaces/icon";
import SelectableCard from "../../../../components/cards/selectableCard/SelectableCard";
import Restricted from "../../../Application/Restricted";
import { PublishedStatusTypes, RolePermissions, RouteNames } from "../../../../enums";
import { PackStatusActionLabel } from "../PackStatusActionLabel/PackStatusActionLabel";
import { PackPublisherInfo } from "../../../../components/packs/packPublisherInfo/PackPublisherInfo";
import { PackArchivedLabel } from "../../../../components/packs/packsList/packArchivedLabel/PackArchivedLabel";
import { type PublisherInfo } from "features/Licensing/Packs/Overview/types";
import { type PackOverview } from "../types/state";
import { PackPrivateLabel } from "../../../../components/packs/packPrivateLabel/PackPrivateLabel";
import { type AccountTypeEnum } from "../../../../interfaces/Account";

import "./packCard.scss";
import { PackPrice } from "components/packs/packPrice/PackPrice";
import { getIsTrialAllowed, getTrialPeriod } from "../utils/packUtils";
import { usePackActions } from "./hooks/usePackActions";
import { usePackLabel } from "./hooks/usePackLabel";
import { Tooltip } from "components/common/tooltip";
import { TextTruncate } from "components";

export interface PackCardProps {
  item: PackOverview;
  selected: boolean;
  disabled: boolean;
  onSelect: (id: number) => void;
  onTitleClick: (id: number, isOwn: boolean, isPurchased: boolean, publisherInfo: PublisherInfo) => void;
  onPreview: (id: number, publisherInfo: PublisherInfo) => void;
  showPackRequestModal: (id: number, title: string, isTrial: boolean, trialDuration: number) => void;
  onEdit: (id: number, isDraft?: boolean) => void;
  onDelete: (id: number) => void;
  icons?: Icon[];
  hasPurchasePackAddon: boolean;
  accountTypeId: AccountTypeEnum;
  parentAccountTypeId: AccountTypeEnum | null;
}

function PackCard(props: PackCardProps) {
  const {
    onSelect,
    onEdit,
    onDelete,
    onTitleClick,
    onPreview,
    icons,
    showPackRequestModal,
    item,
    hasPurchasePackAddon,
    accountTypeId,
    parentAccountTypeId,
  } = props;

  const {
    id,
    thumbnailImageUrl,
    title,
    description,
    isDraft,
    isOwn,
    isRequested,
    isPurchased,
    isTrial,
    isExpired,
    publisher,
    logoUri,
    isArchived,
    isFromTemplate,
    isPrivate,
    pricePerUserPerMonth,
    packPricingTypeId,
  } = item;

  const trialPeriod = useMemo(() => getTrialPeriod(item), [item]);
  const actions = usePackActions(item, { onEdit, onDelete, onPreview, showPackRequestModal });
  const label = usePackLabel(item);

  const onCardClick = () => {
    onTitleClick && onTitleClick(id, isOwn, isPurchased, { name: publisher, logoUri });
  };

  const renderBottomRowContent = () => {
    if (isOwn) {
      return (
        <>
          <div className="price-row">
            <PackPrice
              price={pricePerUserPerMonth}
              packPricingTypeId={packPricingTypeId}
              disabled={isArchived}
              accountTypeId={accountTypeId}
              parentAccountTypeId={parentAccountTypeId}
            />
          </div>
          <div className="bottom-row">
            <PublishStatus publishedStatus={status} />
            <Restricted permissions={[RolePermissions.PacksManage, RolePermissions.FlowsCreate]}>
              <div onClick={(e: SyntheticEvent) => e.preventDefault()}>
                <EllipsisPopupButton circle>{actions.ownPackMenu}</EllipsisPopupButton>
              </div>
            </Restricted>
          </div>
        </>
      );
    }
    return (
      <>
        <div className="bottom-row">
          <div>
            {!isRequested && (
              <PackPrice
                price={pricePerUserPerMonth}
                packPricingTypeId={packPricingTypeId}
                disabled={isArchived}
                accountTypeId={accountTypeId}
                parentAccountTypeId={parentAccountTypeId}
              />
            )}
          </div>
          <PackStatusActionLabel
            isExpired={isExpired}
            isArchived={isArchived}
            isOwn={isOwn}
            isPurchased={isPurchased}
            isRequested={isRequested}
            isTrialAllowed={getIsTrialAllowed(item)}
            isTrial={isTrial}
            packPricingTypeId={packPricingTypeId}
            trialPeriodName={trialPeriod?.name}
            hasPurchasePackAddon={hasPurchasePackAddon}
            isFromTemplate={isFromTemplate}
            isPrivate={isPrivate}
            minimalStatus
            onRequestClick={(e: SyntheticEvent) => {
              actions.openPackRequestModal(e, false);
            }}
            onTrialClick={(e: SyntheticEvent) => {
              actions.openPackRequestModal(e, true);
            }}
          />
          {isPurchased && !isTrial && !isExpired && (
            <EllipsisPopupButton circle>{actions.availablePackMenu}</EllipsisPopupButton>
          )}
        </div>
      </>
    );
  };

  const status = isDraft ? PublishedStatusTypes.draft : PublishedStatusTypes.published;
  return (
    <SelectableCard
      id={id}
      className={cn("pack-card")}
      onCardClick={isArchived ? null : onCardClick}
      onSelect={onSelect}
      disabled={isArchived}
      url={isOwn ? `/${RouteNames.licensingPacks}/${id}` : ""}
    >
      <CardThumbnail
        thumbnailUrl={thumbnailImageUrl}
        disabled={isArchived}
        label={label?.name}
        labelColor={label?.color}
      >
        <div className="label-row">
          <Restricted permissions={[RolePermissions.PacksManage]}>
            {isOwn && <PackPrivateLabel isPrivate={isPrivate} />}
          </Restricted>
        </div>
      </CardThumbnail>
      <CardContent
        title={title}
        icons={icons}
        preHeader={<PackPublisherInfo publisherName={publisher} disabled={isArchived} />}
        postHeader={<PackArchivedLabel isArchived={isArchived} />}
        disabled={isArchived}
        skipDefaultFiller
      >
        <Tooltip
          target={
            <TextTruncate className="description-row" lines={3}>
              {description}
            </TextTruncate>
          }
          content={description}
          position="top center"
        />
        <div className="filler" />
        {renderBottomRowContent()}
      </CardContent>
    </SelectableCard>
  );
}

export default PackCard;
